import { ToastProvider, type ToastPosition } from "@salestrak/ui";
import React from "react";
import { useUserSettings } from "#app/lib/hooks/use-user-settings";

/** Anchor used when no position is passed / no user preference is stored. */
export const DEFAULT_TOAST_POSITION: ToastPosition = "bottom-right";

/**
 * The six anchors with display labels — render these as the choices anywhere
 * the position is user-configurable (the preferences screen's Select). This
 * file is their canonical home: it owns the viewport that honors them.
 */
export const TOAST_POSITION_OPTIONS: ReadonlyArray<{
  label: string;
  value: ToastPosition;
}> = [
  { label: "Top left", value: "top-left" },
  { label: "Top center", value: "top-center" },
  { label: "Top right", value: "top-right" },
  { label: "Bottom left", value: "bottom-left" },
  { label: "Bottom center", value: "bottom-center" },
  { label: "Bottom right", value: "bottom-right" },
];

const VALID_POSITIONS: ReadonlySet<string> = new Set(
  TOAST_POSITION_OPTIONS.map((o) => o.value),
);

/**
 * Resolves a stored position preference to a usable anchor. Preferences
 * travel as untyped strings (null while unloaded, arbitrary if the stored
 * row was ever hand-edited), so anything outside the six anchors falls back
 * to the default.
 */
export function resolveToastPosition(
  pref: string | null | undefined,
): ToastPosition {
  return pref && VALID_POSITIONS.has(pref)
    ? (pref as ToastPosition)
    : DEFAULT_TOAST_POSITION;
}

/**
 * App-level toast viewport: drives the library `ToastProvider` from the user's
 * toastPosition preference. Must render inside AppProviders (needs Apollo);
 * falls back to the default anchor while logged out / loading / on an invalid
 * stored value. ONE provider, ONE viewport — never mount a second
 * `ToastProvider`, or `toastManager` toasts render twice.
 */
export function AppToastProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { preferences } = useUserSettings();
  return (
    <ToastProvider position={resolveToastPosition(preferences?.toastPosition)}>
      {children}
    </ToastProvider>
  );
}
